<template>
  <div class="row">
    <div class="col-sm-12">
      <div class="app-select-wrapper" @keyup.esc="outClick" v-click-outside="outClick">
        <input
          type="search"
          class="app-select-search form-control"
          v-bind="displayValue !== null ? { value: isResultListActive ? searchQuery : displayValue } : {}"
          @focus="open"
          @click="open"
          @input="search"
          :placeholder="placeholder"
          @keyup.down="onArrowDown"
          @keyup.up="onArrowUp"
          @keyup.enter="onEnter"
        />
        <div v-if="isResultListActive" class="app-select-result" :style="listHeightStyle" @scroll="pageLoader" ref="list">
          <div class="app-select-item" @click.stop.prevent="select(searchQuery)" v-if="searchQuery && proposeNew">
            <b>{{ searchQuery }}</b> (create new)
          </div>
          <div
            class="app-select-item"
            v-for="(item, i) in innerItems"
            :key="item.id"
            :class="{ 'is-active': i === itemPositionCounter }"
            ref="scrollItems"
            @click="select(item)"
            @mouseover="itemPositionCounter = i"
          >
            <slot :item="item"></slot>
          </div>
          <div class="app-select-item text-muted empty-results" v-if="!proposeNew && !innerItems.length">
            not found
          </div>
          <div ref="bottom" class="list-bottom"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppSelect',
  props: {
    items: Array,
    placeholder: String,
    proposeNew: {
      type: Boolean,
      default: true
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    displayValue: {
      type: String,
      default: null
    }
  },
  data: () => ({
    value: '',
    searchQuery: '',
    searchActive: false,
    itemsStore: {},
    itemsStoreList: [],
    pixelsToScrollEnd: 10,
    itemPositionCounter: -1,
    listHeightStyle: ''
  }),
  computed: {
    isResultListActive() {
      return this.searchActive // && (this.searchQuery.length || this.innerItems.length)
    },
    innerItems() {
      return this.itemsStoreList.map((id) => this.itemsStore[id])
    }
  },
  methods: {
    select(item) {
      if (this.closeOnSelect) this.searchActive = false
      this.$emit('select', item)
    },
    /**
     * Mirrors the items prop, de-duplicated by id and keeping the order given.
     *
     * The store used to be added to rather than rebuilt, which cost three things: a
     * narrowed search kept rows that no longer matched, an id that came back with new
     * contents went on showing whichever version arrived first, and the keys were written
     * straight onto the object where Vue 2 cannot see them. Rebuilding is safe because
     * every caller passes the whole list it holds - a further page arrives as the
     * accumulated array, not as a delta.
     */
    absorb(items) {
      const store = {}
      const order = []
      for (const item of items || []) {
        if (!Object.hasOwn(store, item.id)) order.push(item.id)
        store[item.id] = item
      }
      this.itemsStore = store
      this.itemsStoreList = order
    },
    search(e) {
      if (e) this.searchQuery = e.target.value || ''
      this.itemPositionCounter = -1
      // The list is not blanked here. The caller answers with a fresh items array and the
      // watcher rebuilds from it; emptying it first only flashed "not found" into the gap,
      // and left a caller that filters its own list showing nothing at all.
      this.$emit('search', this.searchQuery)
    },
    outClick() {
      this.searchActive = false
      this.itemPositionCounter = -1
    },
    pageLoader(e) {
      const el = e.target
      // if less then pixelsToScrollEnd left load next page
      if (el.scrollHeight - el.scrollTop - el.clientHeight < this.pixelsToScrollEnd) {
        this.$emit('nextPage', this.searchQuery)
      }
    },
    open() {
      // open() is wired to both focus and click, so one press on a closed field ran it
      // twice: two identical requests, and two synchronous wipes of a list that only
      // refills when the answers arrive. Clicking an already-open field did the same
      // again, emptying the list under the pointer. Opening is a one-way trip.
      if (this.searchActive) return

      // While the list is closed a displayValue field shows the selection, not the term that
      // was typed last time - so a term left behind is invisible and would silently keep the
      // list filtered by something the user cannot see or clear. Start such a field clean.
      if (this.displayValue !== null) {
        this.searchQuery = ''
      }
      this.search()
      this.searchActive = true
    },
    onArrowDown(e) {
      if (this.itemPositionCounter < this.itemsStoreList.length - 1) {
        this.itemPositionCounter = this.itemPositionCounter + 1
        this.$refs.scrollItems[this.itemPositionCounter].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        })
      }
    },
    onArrowUp() {
      if (this.itemPositionCounter > 0) {
        this.itemPositionCounter = this.itemPositionCounter - 1
        this.$refs.scrollItems[this.itemPositionCounter].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        })
      }
    },
    onEnter() {
      if (this.itemPositionCounter >= 0) {
        const itemId = this.itemsStoreList[this.itemPositionCounter]
        const itemObj = this.itemsStore[itemId]
        this.$emit('select', itemObj)
        this.itemPositionCounter = -1
      }
    },
    onBottomViewportCross(entries) {
      if (entries[0]) {
        const heightDiff = window.innerHeight - entries[0].boundingClientRect.bottom
        const list = entries[0].target.parentElement
        if (heightDiff < 0) {
          const listRect = list.getBoundingClientRect()
          list.style.height = `${listRect.height + window.innerHeight - listRect.bottom - 3}px`
        } else {
          // turn off height adjust
          list.style.height = ''
        }
      }
    }
  },
  mounted() {
    this.search() // empty search for init items
  },
  watch: {
    items: {
      // immediate, because a caller that already holds its list hands it over before mount
      // and the watcher would otherwise only ever see later changes - those items stayed
      // invisible and the field read "not found" until something replaced the array
      immediate: true,
      handler(newItems) {
        this.absorb(newItems)
      }
    },
    isResultListActive: {
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            const observer = new IntersectionObserver(this.onBottomViewportCross, { threshold: 1 })
            observer.observe(this.$refs.bottom)
          })
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.app-select-wrapper {
  position: relative;
}

.app-select-search {
  text-overflow: ellipsis;
}

.app-select-result {
  display: flex;
  flex-direction: column;
  position: absolute;
  border: 1px solid #aaa;
  top: 34px;
  width: 100%;
  background: #fff;
  max-height: 30vh;
  overflow-y: scroll;
  z-index: 999;
}

.app-select-item {
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  background: #fff;
}

.is-active {
  background-color: #4680ff;
  color: white;
}

.empty-results:hover {
  color: inherit;
  background: inherit;
}

.list-bottom {
  height: 1px;
}
</style>
